import { useEffect, useRef } from 'react'
import { latLonToVec3, spinForLon } from './projection'
import { isLand } from './land-mask'
import { GLOBE_RAMP_RGB, type RGB } from './ramp'
import type { GlobePoint } from './useGlobeData'
import type { ThreatLevel } from '@/lib/criteria/levels'
import './globe.css'

/*
 * The DOT GLOBE — a halftone dotted sphere on canvas 2D, no dependencies.
 *
 * • A full equal-area dot field gives the sphere its form: dim grey ocean dots +
 *   bright cool-blue/white-grey land dots (front hemisphere only, foreshortened &
 *   limb-darkened for depth). The land/ocean split comes from a baked Natural Earth
 *   mask (land-mask.ts) — sampled once, cached for both globe instances.
 * • Live weather points (tropical systems, SPC, NWS alerts) glow + pulse on top,
 *   coloured on the threat ramp. Tap one → onSelect (the parent routes it).
 * • The user's location is a distinct accent marker with a soft halo.
 *
 * Performance: one rAF loop. The dot field draws as two batched paths; weather-point
 * glows are pre-rendered sprites (drawImage, no per-frame gradient); the sphere's
 * volume/limb gradients are cached per size; hit targets reuse a pool. The hot loop
 * allocates nothing. The loop runs only while on-screen AND tab-visible AND motion is
 * allowed; otherwise it paints a single static frame and idles the compositor.
 */

const TAU = Math.PI * 2
const TILT_DEFAULT = 0.36 // ~ +20°: tip the northern hemisphere toward the viewer (US-centric)
const ROLL_DEFAULT = 0 // axial roll (slant the N–S axis); home passes a tilted value
const SPIN_RATE = 0.05 // rad/s — slow auto-rotation
const PULSE_RATE = 2.2 // rad/s — weather-point pulse
const DRAG_RESUME_MS = 2600 // pause auto-rotation this long after a drag

// ── Shared dot field (built once, reused by every globe instance) ──────────────
let FIELD: { land: Float32Array; ocean: Float32Array } | null = null
function getField() {
  if (FIELD) return FIELD
  const land: number[] = []
  const ocean: number[] = []
  const LAT_STEP = 2.0 // ° between rings
  const EQUATOR_DOTS = 180 // dots on the equator ring → ~square spacing
  let ring = 0
  for (let lat = -88; lat <= 88; lat += LAT_STEP, ring++) {
    const cphi = Math.cos((lat * Math.PI) / 180)
    const count = Math.max(1, Math.round(EQUATOR_DOTS * cphi))
    const offset = (ring * 0.6) % 1 // stagger rings to kill vertical seams
    for (let k = 0; k < count; k++) {
      const lon = -180 + ((k + offset) / count) * 360
      const v = latLonToVec3(lat, lon)
      if (isLand(lat, lon)) {
        land.push(v.x, v.y, v.z)
      } else if ((k + ring) % 5 < 2) {
        // subsample ocean (~40%) → land reads forward, lighter to draw
        ocean.push(v.x, v.y, v.z)
      }
    }
  }
  FIELD = { land: new Float32Array(land), ocean: new Float32Array(ocean) }
  return FIELD
}

// ── Colours: weather points ride the brand-blue intensity ramp (ramp.ts); only the
// user marker reads the accent token. ──────────────────────────────────────────
function hexToRgb(s: string): RGB | null {
  const v = s.trim()
  const m = v.match(/^#?([0-9a-f]{6})$/i) || v.match(/^#?([0-9a-f]{3})$/i)
  if (m) {
    const h = m[1]
    const f = h.length === 3 ? h.replace(/(.)/g, '$1$1') : h
    return [parseInt(f.slice(0, 2), 16), parseInt(f.slice(2, 4), 16), parseInt(f.slice(4, 6), 16)]
  }
  const rgb = v.match(/(\d+)[,\s]+(\d+)[,\s]+(\d+)/)
  if (rgb) return [+rgb[1], +rgb[2], +rgb[3]]
  return null
}
function resolveAccent(): RGB {
  if (typeof getComputedStyle !== 'function') return [96, 165, 250]
  return (
    hexToRgb(getComputedStyle(document.documentElement).getPropertyValue('--accent-bright')) ?? [
      96, 165, 250,
    ]
  )
}

/** A radial glow sprite for a colour — built once, blitted per point with drawImage. */
function makeGlow([r, g, b]: RGB): HTMLCanvasElement {
  const s = 64
  const c = document.createElement('canvas')
  c.width = c.height = s
  const ctx = c.getContext('2d')!
  const grad = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2)
  grad.addColorStop(0, `rgba(${r},${g},${b},0.95)`)
  grad.addColorStop(0.4, `rgba(${r},${g},${b},0.34)`)
  grad.addColorStop(1, `rgba(${r},${g},${b},0)`)
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, s, s)
  return c
}

const LAND_RGB = '198,210,226' // cool white-grey
const OCEAN_RGB = '88,104,126' // dim cool grey-blue

function hashId(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h)
}
const phaseFor = (id: string) => ((hashId(id) % 1000) / 1000) * TAU

interface HitTarget {
  x: number
  y: number
  r: number
  point: GlobePoint | null
}

export interface DotGlobeProps {
  points: GlobePoint[]
  userLat?: number
  userLon?: number
  onSelect?: (p: GlobePoint) => void
  className?: string
  /** Tip the northern hemisphere toward the viewer (rad). Default ~+20°. */
  tilt?: number
  /** Slant the N–S axis in the view plane (rad) for the angled, full-bleed look. */
  roll?: number
  /** Accessible description for the canvas. */
  ariaLabel?: string
}

interface GlobeState {
  spin: number
  dpr: number
  pausedUntil: number
  cx: number
  cy: number
  R: number
  baseDot: number
  volGrad: CanvasGradient | null
  limbGrad: CanvasGradient | null
}

export function DotGlobe({
  points,
  userLat,
  userLon,
  onSelect,
  className,
  tilt = TILT_DEFAULT,
  roll = ROLL_DEFAULT,
  ariaLabel,
}: DotGlobeProps) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  // Live props read by the long-lived rAF loop without restarting it.
  const pointsRef = useRef(points)
  pointsRef.current = points
  const userRef = useRef({ userLat, userLon })
  userRef.current = { userLat, userLon }
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect
  const cfgRef = useRef({ tilt, roll })
  cfgRef.current = { tilt, roll }

  const redrawRef = useRef<() => void>(() => {})
  const stateRef = useRef<GlobeState | null>(null)
  if (!stateRef.current) {
    stateRef.current = {
      spin: typeof userLon === 'number' ? spinForLon(userLon) : spinForLon(-30),
      dpr: 1,
      pausedUntil: 0,
      cx: 0,
      cy: 0,
      R: 0,
      baseDot: 0,
      volGrad: null,
      limbGrad: null,
    }
  }

  useEffect(() => {
    const wrap = wrapRef.current
    const canvas = canvasRef.current
    const st = stateRef.current
    if (!wrap || !canvas || !st) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const field = getField()
    const colors = GLOBE_RAMP_RGB
    const accent = resolveAccent()
    const glow: Record<ThreatLevel, HTMLCanvasElement> = {
      quiet: makeGlow(colors.quiet),
      info: makeGlow(colors.info),
      watch: makeGlow(colors.watch),
      warning: makeGlow(colors.warning),
      severe: makeGlow(colors.severe),
      extreme: makeGlow(colors.extreme),
    }
    const accentGlow = makeGlow(accent)
    const reduce =
      typeof matchMedia === 'function'
        ? matchMedia('(prefers-reduced-motion: reduce)')
        : ({ matches: false, addEventListener: () => {}, removeEventListener: () => {} } as unknown as MediaQueryList)

    // Pooled hit targets — reused every frame (no per-frame allocation).
    const hitPool: HitTarget[] = []
    let hitCount = 0

    let raf = 0
    let running = false
    let last = 0
    let inView = true
    let visible = typeof document === 'undefined' ? true : !document.hidden

    function draw(t: number) {
      if (!st!.R) return
      const dpr = st!.dpr
      const cx = st!.cx
      const cy = st!.cy
      const R = st!.R
      const baseDot = st!.baseDot
      const W = cx * 2
      const H = cy * 2
      const spin = st!.spin
      const ss = Math.sin(spin)
      const cs = Math.cos(spin)
      const stl = Math.sin(cfgRef.current.tilt)
      const ctl = Math.cos(cfgRef.current.tilt)
      const sr = Math.sin(cfgRef.current.roll)
      const cr = Math.cos(cfgRef.current.roll)

      ctx!.clearRect(0, 0, W, H)

      // sphere volume (cached gradient)
      if (st!.volGrad) {
        ctx!.fillStyle = st!.volGrad
        ctx!.beginPath()
        ctx!.arc(cx, cy, R, 0, TAU)
        ctx!.fill()
      }

      // ocean dots (one batched path)
      const ocean = field.ocean
      ctx!.fillStyle = `rgb(${OCEAN_RGB})`
      ctx!.globalAlpha = 0.2
      ctx!.beginPath()
      for (let i = 0; i < ocean.length; i += 3) {
        const vx = ocean[i]
        const vy = ocean[i + 1]
        const vz = ocean[i + 2]
        const z1 = -vx * ss + vz * cs
        const z2 = vy * stl + z1 * ctl
        if (z2 <= 0.02) continue
        const x1 = vx * cs + vz * ss
        const y2 = vy * ctl - z1 * stl
        const sx = cx + (x1 * cr - y2 * sr) * R
        const sy = cy - (x1 * sr + y2 * cr) * R
        const r = baseDot * (0.3 + 0.55 * z2)
        ctx!.moveTo(sx + r, sy)
        ctx!.arc(sx, sy, r, 0, TAU)
      }
      ctx!.fill()

      // land dots (one batched path)
      const land = field.land
      ctx!.fillStyle = `rgb(${LAND_RGB})`
      ctx!.globalAlpha = 0.92
      ctx!.beginPath()
      for (let i = 0; i < land.length; i += 3) {
        const vx = land[i]
        const vy = land[i + 1]
        const vz = land[i + 2]
        const z1 = -vx * ss + vz * cs
        const z2 = vy * stl + z1 * ctl
        if (z2 <= 0.02) continue
        const x1 = vx * cs + vz * ss
        const y2 = vy * ctl - z1 * stl
        const sx = cx + (x1 * cr - y2 * sr) * R
        const sy = cy - (x1 * sr + y2 * cr) * R
        const r = baseDot * (0.5 + 0.85 * z2)
        ctx!.moveTo(sx + r, sy)
        ctx!.arc(sx, sy, r, 0, TAU)
      }
      ctx!.fill()
      ctx!.globalAlpha = 1

      // limb darkening → 3D read (cached gradient)
      if (st!.limbGrad) {
        ctx!.fillStyle = st!.limbGrad
        ctx!.beginPath()
        ctx!.arc(cx, cy, R, 0, TAU)
        ctx!.fill()
      }

      // crisp rim
      ctx!.strokeStyle = 'rgba(122,150,190,0.2)'
      ctx!.lineWidth = Math.max(1, dpr * 0.6)
      ctx!.beginPath()
      ctx!.arc(cx, cy, R, 0, TAU)
      ctx!.stroke()

      // weather points
      const pts = pointsRef.current
      const rm = reduce.matches
      hitCount = 0
      for (const p of pts) {
        const v = latLonToVec3(p.lat, p.lon)
        const z1 = -v.x * ss + v.z * cs
        const z2 = v.y * stl + z1 * ctl
        if (z2 <= 0.02) continue
        const x1 = v.x * cs + v.z * ss
        const y2 = v.y * ctl - z1 * stl
        const sx = cx + (x1 * cr - y2 * sr) * R
        const sy = cy - (x1 * sr + y2 * cr) * R
        const [pr, pg, pb] = colors[p.level]
        const depth = 0.6 + 0.4 * z2
        const pulse = rm ? 0.6 : 0.5 + 0.5 * Math.sin(t * PULSE_RATE + phaseFor(p.id))
        const core = baseDot * 1.9 * depth
        const halo = core * (2.4 + 1.5 * pulse)
        ctx!.globalAlpha = 0.55 * (0.6 + 0.4 * pulse)
        ctx!.drawImage(glow[p.level], sx - halo, sy - halo, halo * 2, halo * 2)
        ctx!.globalAlpha = 1
        ctx!.fillStyle = `rgba(${pr},${pg},${pb},0.95)`
        ctx!.beginPath()
        ctx!.arc(sx, sy, core, 0, TAU)
        ctx!.fill()
        ctx!.fillStyle = 'rgba(255,255,255,0.85)'
        ctx!.beginPath()
        ctx!.arc(sx, sy, core * 0.42, 0, TAU)
        ctx!.fill()
        let h = hitPool[hitCount]
        if (!h) {
          h = { x: 0, y: 0, r: 0, point: null }
          hitPool[hitCount] = h
        }
        h.x = sx / dpr
        h.y = sy / dpr
        h.r = Math.max(halo, baseDot * 6) / dpr
        h.point = p
        hitCount++
      }

      // user marker
      const u = userRef.current
      if (typeof u.userLat === 'number' && typeof u.userLon === 'number') {
        const v = latLonToVec3(u.userLat, u.userLon)
        const z1 = -v.x * ss + v.z * cs
        const z2 = v.y * stl + z1 * ctl
        if (z2 > 0.02) {
          const x1 = v.x * cs + v.z * ss
          const y2 = v.y * ctl - z1 * stl
          const sx = cx + (x1 * cr - y2 * sr) * R
          const sy = cy - (x1 * sr + y2 * cr) * R
          const pulse = rm ? 0.6 : 0.5 + 0.5 * Math.sin(t * 1.6)
          const [ar, ag, ab] = accent
          const halo = baseDot * (4.5 + 2.5 * pulse)
          ctx!.globalAlpha = 0.85
          ctx!.drawImage(accentGlow, sx - halo, sy - halo, halo * 2, halo * 2)
          ctx!.globalAlpha = 1
          ctx!.strokeStyle = `rgba(${ar},${ag},${ab},0.95)`
          ctx!.lineWidth = Math.max(1.5, dpr)
          ctx!.beginPath()
          ctx!.arc(sx, sy, baseDot * 2.3, 0, TAU)
          ctx!.stroke()
          ctx!.fillStyle = 'rgba(255,255,255,0.96)'
          ctx!.beginPath()
          ctx!.arc(sx, sy, baseDot * 1.05, 0, TAU)
          ctx!.fill()
        }
      }
    }

    function drawOnce() {
      draw(typeof performance !== 'undefined' ? performance.now() / 1000 : 0)
    }
    // Repaint the static frame on data change ONLY when the loop is idle; while it's
    // running the next rAF frame already picks up the new data via pointsRef.
    redrawRef.current = () => {
      if (!running) drawOnce()
    }

    function frame(now: number) {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0
      last = now
      if (now > st!.pausedUntil) st!.spin += SPIN_RATE * dt
      draw(now / 1000)
      if (running) raf = requestAnimationFrame(frame)
    }
    function start() {
      if (running || reduce.matches) return
      running = true
      last = 0
      raf = requestAnimationFrame(frame)
    }
    function stop() {
      running = false
      if (raf) cancelAnimationFrame(raf)
      raf = 0
    }
    function maybeRun() {
      if (inView && visible && !reduce.matches) start()
      else {
        stop()
        drawOnce()
      }
    }

    function resize() {
      const rect = wrap!.getBoundingClientRect()
      if (!rect.width || !rect.height) return
      const dpr = Math.min(2, typeof devicePixelRatio === 'number' ? devicePixelRatio : 1)
      // Backing store matches the actual CSS box on BOTH axes → no stretch, and the
      // device→CSS mapping is a uniform /dpr (correct hit-testing). The disc is a
      // centred circle sized to the smaller axis, so a non-square box never distorts it.
      canvas!.width = Math.round(rect.width * dpr)
      canvas!.height = Math.round(rect.height * dpr)
      const W = canvas!.width
      const H = canvas!.height
      st!.dpr = dpr
      st!.cx = W / 2
      st!.cy = H / 2
      st!.R = (Math.min(W, H) / 2) * 0.92
      st!.baseDot = st!.R * 0.0105
      const cx = st!.cx
      const cy = st!.cy
      const R = st!.R
      const vg = ctx!.createRadialGradient(cx - R * 0.34, cy - R * 0.44, R * 0.04, cx, cy, R * 1.02)
      vg.addColorStop(0, 'rgba(58,70,90,0.55)')
      vg.addColorStop(0.6, 'rgba(28,33,42,0.32)')
      vg.addColorStop(1, 'rgba(10,12,16,0)')
      st!.volGrad = vg
      const lg = ctx!.createRadialGradient(cx, cy, R * 0.55, cx, cy, R)
      lg.addColorStop(0, 'rgba(19,21,25,0)')
      lg.addColorStop(0.8, 'rgba(19,21,25,0)')
      lg.addColorStop(1, 'rgba(19,21,25,0.7)')
      st!.limbGrad = lg
      drawOnce()
    }

    // ── interaction: tap to select, horizontal drag to spin (vertical scrolls) ──
    const drag = { id: -1, downX: 0, downY: 0, lastX: 0, active: false }
    function onDown(e: PointerEvent) {
      drag.id = e.pointerId
      drag.downX = e.clientX
      drag.downY = e.clientY
      drag.lastX = e.clientX
      drag.active = false
    }
    function onMove(e: PointerEvent) {
      if (drag.id !== e.pointerId) return
      const dx = e.clientX - drag.lastX
      drag.lastX = e.clientX
      const totalX = Math.abs(e.clientX - drag.downX)
      const totalY = Math.abs(e.clientY - drag.downY)
      if (!drag.active && totalX > 8 && totalX > totalY) {
        drag.active = true
        canvas!.setPointerCapture?.(e.pointerId)
      }
      if (drag.active) {
        e.preventDefault()
        st!.spin += dx * 0.01
        st!.pausedUntil = (typeof performance !== 'undefined' ? performance.now() : 0) + DRAG_RESUME_MS
        if (!running) drawOnce()
      }
    }
    function onUp(e: PointerEvent) {
      if (drag.id !== e.pointerId) return
      const wasDrag = drag.active
      drag.id = -1
      drag.active = false
      if (wasDrag) return
      const rect = canvas!.getBoundingClientRect()
      const px = e.clientX - rect.left
      const py = e.clientY - rect.top
      let best: GlobePoint | null = null
      let bestD = Infinity
      for (let i = 0; i < hitCount; i++) {
        const h = hitPool[i]
        const d = (h.x - px) ** 2 + (h.y - py) ** 2
        if (d < h.r * h.r && d < bestD) {
          bestD = d
          best = h.point
        }
      }
      if (best) onSelectRef.current?.(best)
    }

    const io =
      typeof IntersectionObserver === 'function'
        ? new IntersectionObserver(
            ([e]) => {
              inView = e.isIntersecting
              maybeRun()
            },
            { rootMargin: '100px' },
          )
        : null
    io?.observe(wrap)
    const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(resize) : null
    ro?.observe(wrap)
    const onVis = () => {
      visible = !document.hidden
      maybeRun()
    }
    document.addEventListener('visibilitychange', onVis)
    const onReduceChange = () => maybeRun()
    reduce.addEventListener?.('change', onReduceChange)

    canvas.addEventListener('pointerdown', onDown)
    canvas.addEventListener('pointermove', onMove)
    canvas.addEventListener('pointerup', onUp)
    canvas.addEventListener('pointercancel', onUp)

    resize()
    maybeRun()

    return () => {
      stop()
      io?.disconnect()
      ro?.disconnect()
      document.removeEventListener('visibilitychange', onVis)
      reduce.removeEventListener?.('change', onReduceChange)
      canvas.removeEventListener('pointerdown', onDown)
      canvas.removeEventListener('pointermove', onMove)
      canvas.removeEventListener('pointerup', onUp)
      canvas.removeEventListener('pointercancel', onUp)
    }
  }, [])

  // Repaint when data changes while the loop is idle (gated inside redrawRef).
  useEffect(() => {
    redrawRef.current()
  }, [points, userLat, userLon])

  return (
    <div ref={wrapRef} className={`dot-globe${className ? ` ${className}` : ''}`}>
      <canvas
        ref={canvasRef}
        className="dot-globe__canvas"
        role="img"
        aria-label={ariaLabel ?? 'Globe of active weather'}
      />
    </div>
  )
}
